import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface User {
  id: number;
  email: string;
  name: string | null;
  phoneNumber: string | null;
  birthDate?: string | null;
  birthTime?: string | null;
  birthPlace?: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isGuestMode: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    name: string,
    phoneNumber: string,
    birthDate?: string | null,
    birthTime?: string | null,
    birthPlace?: string | null
  ) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Usar ruta relativa en producción para evitar problemas de CSP
const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000');

// Configurar axios con interceptor para token
axios.defaults.baseURL = API_URL;
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Solo recargar la página si el error 401 es por token expirado, no por credenciales incorrectas
    if (error.response?.status === 401 && error.config?.url?.includes('/verify')) {
      localStorage.removeItem('token');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isGuestMode, setIsGuestMode] = useState(false);

  useEffect(() => {
    const initializeAuth = async () => {
      // Verificar si guest mode está activado
      try {
        const configResponse = await axios.get(`${API_URL}/api/auth/config`);
        const guestModeEnabled = configResponse.data.guestMode;
        setIsGuestMode(guestModeEnabled);

        if (guestModeEnabled) {
          // Modo guest: crear usuario temporal
          const guestId = generateGuestId();
          const guestUser: User = {
            id: guestId,
            email: `guest_${guestId}@guest.com`,
            name: 'Visitante',
            phoneNumber: null,
            birthDate: null,
            birthTime: null,
            birthPlace: null
          };
          setUser(guestUser);
          setLoading(false);
        } else {
          // Modo normal: verificar token existente
          const token = localStorage.getItem('token');
          if (token) {
            verifyToken(token);
          } else {
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('Error al obtener configuración:', error);
        // En caso de error, intentar modo normal
        const token = localStorage.getItem('token');
        if (token) {
          verifyToken(token);
        } else {
          setLoading(false);
        }
      }
    };

    initializeAuth();
  }, []);

  // Generar ID único para usuario guest
  const generateGuestId = (): number => {
    // Usar timestamp + número aleatorio para crear ID único
    // Usar número negativo para distinguir guests de usuarios reales
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    // Generar número negativo único: -[timestamp][random]
    // Convertir timestamp a string y tomar últimos dígitos
    const timestampStr = timestamp.toString();
    const guestIdStr = `-${timestampStr.slice(-10)}${random.toString().padStart(3, '0')}`;
    // Parsear a número (será negativo, aprox -10 dígitos)
    return parseInt(guestIdStr);
  };

  const verifyToken = async (token: string) => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.valid) {
        setUser(response.data.user);
      } else {
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('Error al verificar token:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      setUser(userData);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Error al iniciar sesión';
      throw new Error(message);
    }
  };

  const register = async (
    email: string,
    password: string,
    name: string,
    phoneNumber: string,
    birthDate?: string | null,
    birthTime?: string | null,
    birthPlace?: string | null
  ) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, { 
        email, 
        password, 
        name, 
        phoneNumber,
        birthDate: birthDate ?? null,
        birthTime: birthTime ?? null,
        birthPlace: birthPlace ?? null
      });
      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      setUser(userData);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Error al registrarse';
      throw new Error(message);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const refreshUser = async () => {
    if (isGuestMode) return;
    const token = localStorage.getItem('token');
    if (token) {
      await verifyToken(token);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isGuestMode,
    login,
    register,
    logout,
    refreshUser,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};
